import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { AppReviewService } from './app-review.service';
import { Modal, ModalType, NonPersistedModalName } from '../models/modal.model';
import { ParamModalEnum } from '../models/queryParam.model';
import { User } from '../models/user.model';
import { EventManager } from '../utils/event-manager';
import { AppService } from '@app/app.service';
import { LoggerService } from '@shared/services/logger.service';
import { LimitedTimeEventService } from './limitedTimeEvent.service';
import { EventsRepository } from '../state/events/events.repository';
import { GamesRepository } from '../state/games/games.repository';
import { AppPermissionsRepository } from '../state/permissions/app-permissions.repository';
import { UserRepository } from '../state/user/user.repository';
import { Throttle } from '../decorators/throttle.decorator';
import { ActionUrlType } from '../models/assets.model';

@Injectable({
  providedIn: 'root',
})
export class RouteParameterInterceptor {
  private readonly appService = inject(AppService);
  private readonly appReviewService = inject(AppReviewService);
  private readonly eventManager = inject(EventManager);
  private readonly gamesRepo = inject(GamesRepository);
  private readonly userRepo = inject(UserRepository);
  private readonly logger = inject(LoggerService);
  private readonly limitedTimeEventService = inject(LimitedTimeEventService);
  private readonly eventsRepo = inject(EventsRepository);
  private readonly appPermissionsRepo = inject(AppPermissionsRepository);

  user: User = this.userRepo.getUser();
  pattern = /^((http|https|ftp):\/\/)/;

  public checkRouteParameters(_route: ActivatedRouteSnapshot) {
    if (!this.routeHasQueryParams(_route)) return;
    this.routeOpenParamModal(parseInt(_route.queryParams.o));
  }

  public checkActionUrl(actionUrlType: ActionUrlType, actionUrl: string, eventId?: string): void {
    switch (actionUrlType) {
      case 'view':
        this.routeOpenParamModal(Number(actionUrl), eventId);
        break;
      case 'game':
        this.showGame(actionUrl);
        break;
      case 'page':
        window.open(actionUrl, '_blank');
        break;
      default:
        break;
    }
  }

  public checkActionUrlOld(actionUrl: string, eventId?: string): void {
    const isNewTab = this.pattern.test(actionUrl);
    const isModal = !isNaN(Number(actionUrl));
    const isGame = !!this.gamesRepo.getGameById(actionUrl);

    if (isNewTab) {
      window.open(actionUrl, '_blank');
    } else if (isModal) {
      this.routeOpenParamModal(Number(actionUrl), eventId);
    } else if (isGame) {
      this.showGame(actionUrl);
    } else {
      return;
    }
  }

  @Throttle(2000)
  private showGame(actionUrl: string): void {
    this.appService.showGame(actionUrl);
  }

  private routeOpenParamModal(_paramModal: ParamModalEnum, eventId?: string) {
    switch (_paramModal) {
      case ParamModalEnum.Shop:
        this.eventManager.openModal(
          new Modal(NonPersistedModalName.ShopModalComponent, ModalType.View, {
            tab: 'left',
          })
        );
        break;
      case ParamModalEnum.DailyChallenge:
        this.eventManager.openModal(new Modal(NonPersistedModalName.DailyChallengeModalComponent, ModalType.View));
        break;
      case ParamModalEnum.Trophy:
        this.eventManager.openModal(new Modal(NonPersistedModalName.UserTrophiesModalComponent, ModalType.View));
        break;
      case ParamModalEnum.LimitedTimeEvent: {
        const event = this.eventsRepo.getEventById(eventId);

        if (!event) return;

        this.limitedTimeEventService.manageOpenLimitedTimeEvents(event);
        break;
      }
      case ParamModalEnum.Ranking:
        this.eventManager.openModal(new Modal(NonPersistedModalName.RankingModal, ModalType.View));
        break;
      case ParamModalEnum.Multievents:
        this.eventManager.openModal(new Modal(NonPersistedModalName.MultieventsModalComponent, ModalType.View));
        break;
      case ParamModalEnum.AppReview:
        this.appReviewService.startReview().catch((e) => this.logger.error(e));
        break;
      case ParamModalEnum.NotificationPermissions:
        this.appPermissionsRepo.requestNotificationsPermission(true).catch((e) => this.logger.error(e));
        break;
      case ParamModalEnum.ProgressLine:
        this.eventManager.openModal(new Modal(NonPersistedModalName.ProgressLineCasinoComponent, ModalType.View));
        break;
      default:
        break;
    }
  }

  private routeHasQueryParams(_route: ActivatedRouteSnapshot): boolean {
    if (!_route.queryParams) return false;
    if (!_route.queryParams.o) return false;
    return true;
  }
}
